<script setup>
/* global axios */
// Popup exibido ao resolver quando falta o evento de conversão (Meta CAPI) exigido.
// Espelha o modal de atributos obrigatórios, mas reusa a UI/lógica do box de conversão
// do ticket: o atendente envia o evento (Lead/Purchase/valor) OU marca "Não converteu";
// ao ter sucesso, emite `submit` (que resolve a conversa, mantendo o context recebido).
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const emit = defineEmits(['submit']);

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;

const dialogRef = ref(null);
const conversationId = ref(null);
const context = ref(null);

const base = computed(
  () =>
    `/api/v1/accounts/${accountId}/conversations/${conversationId.value}/meta_capi`
);

// mesma lista do box de conversão (value = aceita valor/moeda)
const EVENTS = [
  { name: 'Lead', key: 'LEAD', value: false },
  { name: 'Contact', key: 'CONTACT', value: false },
  { name: 'Schedule', key: 'SCHEDULE', value: false },
  { name: 'CompleteRegistration', key: 'COMPLETE_REGISTRATION', value: false },
  { name: 'SubmitApplication', key: 'SUBMIT_APPLICATION', value: false },
  { name: 'ViewContent', key: 'VIEW_CONTENT', value: true },
  { name: 'AddToCart', key: 'ADD_TO_CART', value: true },
  { name: 'InitiateCheckout', key: 'INITIATE_CHECKOUT', value: true },
  { name: 'StartTrial', key: 'START_TRIAL', value: true },
  { name: 'Subscribe', key: 'SUBSCRIBE', value: true },
  { name: 'Purchase', key: 'PURCHASE', value: true },
];

const loading = ref(true);
const referral = ref({});
const acceptedEvents = ref([]); // eventos aceitos (config); [] = qualquer um
const sending = ref(false);
const notConverting = ref(false);
const selected = ref('Lead');
const value = ref('');

const fromAd = computed(
  () => referral.value?.ctwa_clid || referral.value?.source_id
);
const selectedMeta = computed(() =>
  EVENTS.find(e => e.name === selected.value)
);
const supportsValue = computed(() => selectedMeta.value?.value || false);

// se a config restringe eventos, mostra só os aceitos; senão mostra todos
const eventOptions = computed(() => {
  if (!acceptedEvents.value.length) return EVENTS;
  const set = new Set(acceptedEvents.value);
  const filtered = EVENTS.filter(e => set.has(e.name));
  return filtered.length ? filtered : EVENTS;
});

const load = async () => {
  loading.value = true;
  try {
    const { data } = await axios.get(base.value);
    const d = data.data || {};
    referral.value = d.referral || {};
    acceptedEvents.value = d.resolve_requirement?.events || [];
    selected.value = eventOptions.value[0]?.name || 'Lead';
  } catch (e) {
    referral.value = {};
    acceptedEvents.value = [];
  } finally {
    loading.value = false;
  }
};

// envia e, se OK, resolve
const post = async (eventName, extra = {}) => {
  try {
    const { data } = await axios.post(`${base.value}/send`, {
      event_name: eventName,
      ...extra,
    });
    if (data.ok === false) {
      useAlert(t('META_CAPI.BOX.SEND_ERROR', { error: data.error || '' }));
      return false;
    }
    return true;
  } catch (e) {
    useAlert(
      e.response?.data?.error || t('META_CAPI.BOX.SEND_ERROR', { error: '' })
    );
    return false;
  }
};

const close = () => {
  dialogRef.value?.close();
  context.value = null;
};

const finishResolve = () => {
  emit('submit', { context: context.value });
  close();
};

const sendEvent = async () => {
  sending.value = true;
  const extra =
    supportsValue.value && value.value ? { value: value.value } : {};
  const ok = await post(selected.value, extra);
  sending.value = false;
  if (ok) {
    useAlert(t('META_CAPI.BOX.SENT'));
    finishResolve();
  }
};

const markNotConverted = async () => {
  notConverting.value = true;
  const ok = await post('NotConverted');
  notConverting.value = false;
  if (ok) finishResolve();
};

const open = (convId, ctx = null) => {
  conversationId.value = convId;
  context.value = ctx;
  value.value = '';
  load();
  dialogRef.value?.open();
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :show-confirm-button="false"
    :title="t('META_CAPI.RESOLVE_REQUIREMENT.MODAL.TITLE')"
    :description="t('META_CAPI.RESOLVE_REQUIREMENT.MODAL.DESCRIPTION')"
    :cancel-button-label="t('META_CAPI.RESOLVE_REQUIREMENT.MODAL.CANCEL')"
  >
    <div class="flex flex-col gap-3">
      <div v-if="loading" class="text-sm text-n-slate-10">
        {{ t('META_CAPI.BOX.LOADING') }}
      </div>
      <template v-else>
        <!-- origem do anúncio (Click-to-WhatsApp) -->
        <div
          v-if="fromAd"
          class="flex items-start gap-2 p-2 rounded-lg bg-n-alpha-1"
        >
          <span
            class="i-lucide-megaphone size-4 mt-0.5 text-n-teal-11 shrink-0"
          />
          <div class="min-w-0">
            <div class="text-sm text-n-slate-12">
              {{ t('META_CAPI.BOX.FROM_AD') }}
            </div>
            <div
              v-if="referral.headline"
              class="text-xs truncate text-n-slate-10"
            >
              {{ referral.headline }}
            </div>
          </div>
        </div>

        <label class="text-xs text-n-slate-10">{{
          t('META_CAPI.BOX.EVENT_FIELD')
        }}</label>
        <select
          v-model="selected"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option v-for="ev in eventOptions" :key="ev.name" :value="ev.name">
            {{ t(`META_CAPI.EVENTS.${ev.key}`) }}
          </option>
        </select>

        <input
          v-if="supportsValue"
          v-model="value"
          type="number"
          min="0"
          step="0.01"
          :placeholder="t('META_CAPI.BOX.VALUE_PLACEHOLDER')"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        />

        <button
          class="w-full px-3 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:brightness-95 disabled:opacity-50"
          :disabled="sending || notConverting"
          @click="sendEvent"
        >
          {{
            sending
              ? t('META_CAPI.BOX.SENDING')
              : t('META_CAPI.RESOLVE_REQUIREMENT.MODAL.SEND_AND_RESOLVE')
          }}
        </button>

        <button
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-transparent hover:bg-n-alpha-1 hover:text-n-ruby-11 disabled:opacity-50"
          :disabled="sending || notConverting"
          @click="markNotConverted"
        >
          {{
            t('META_CAPI.RESOLVE_REQUIREMENT.MODAL.NOT_CONVERTED_AND_RESOLVE')
          }}
        </button>
      </template>
    </div>
  </Dialog>
</template>
